import { analytics } from '../config/firebase';
import { logEvent } from 'firebase/analytics';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

type EventParams = Record<string, string | number | boolean | undefined>;

class AnalyticsService {
  private static instance: AnalyticsService;

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private trackEvent(eventName: string, params?: EventParams): void {
    try {
      if (analytics) {
        logEvent(analytics, eventName, params);
      }
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, params);
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }

  public pageView(pageName: string, pageLocation?: string): void {
    this.trackEvent('page_view', {
      page_title: pageName,
      page_location: pageLocation || window.location.href,
    });
  }

  public login(method: string): void {
    this.trackEvent('login', { method });
  }

  public signUp(method: string): void {
    this.trackEvent('sign_up', { method });
  }

  public buttonClick(buttonName: string, location?: string): void {
    this.trackEvent('button_click', {
      button_name: buttonName,
      location: location || 'unknown',
    });
  }

  public openSamGov(solicitationId: string, title?: string): void {
    this.trackEvent('open_sam_gov', {
      solicitation_id: solicitationId,
      title: title || '',
    });
  }

  public openExtension(solicitationId: string, title?: string): void {
    this.trackEvent('open_extension', {
      solicitation_id: solicitationId,
      title: title || '',
    });
  }

  public filterChange(filterType: string, filterValue: string): void {
    this.trackEvent('filter_change', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  }

  public subscriptionView(tier?: string): void {
    this.trackEvent('subscription_view', { current_tier: tier || 'none' });
  }

  public subscriptionClick(planName: string): void {
    this.trackEvent('subscription_click', { plan_name: planName });
  }

  public extensionInstallClick(): void {
    this.trackEvent('extension_install_click', {
      location: window.location.pathname,
    });
  }

  public ctaClick(ctaName: string, location: string): void {
    this.trackEvent('cta_click', {
      cta_name: ctaName,
      location: location,
    });
  }

  public searchPerformed(query: string, resultsCount?: number): void {
    this.trackEvent('search', {
      search_term: query,
      results_count: resultsCount,
    });
  }

  public error(errorType: string, errorMessage: string): void {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
    });
  }
}

export const analyticsService = AnalyticsService.getInstance();
